import type { AddPrCommentType } from '@/components/inbox/prs/types/pr-comment-input';
import type { PrV2GithubReviewSide } from '@/lib/pr-v2/types';
import type { DiffNavigationHandlers } from '@/lib/stores/navigation/diff-navigation-store';
import type { PrV2FileDraftState } from '@/lib/stores/pr-v2-draft-store';
import { toast } from 'sonner';

import { commentSideToGithubReviewSide } from '@/lib/pr-v2/comment-side';
import { isPrV2Route } from '@/lib/pr-v2/route-utils';
import { dispatchPrV2TabHandoff } from '@/lib/pr-v2/tab-handoff';
import { PR_V2_TABS } from '@/lib/pr-v2/tabs';
import { PR_TABS } from '@/lib/stores/pr-tab-navigation/types';
import { getPrV2DraftStoreKey } from '@/lib/stores/pr-v2-draft-store';

// ── Shared type guard ───────────────────────────────────────────────

export function hasRequiredAddPrCommentInput(input: unknown): input is AddPrCommentType {
  return (
    typeof input === 'object' &&
    input !== null &&
    'filename' in input &&
    typeof (input as AddPrCommentType).filename === 'string' &&
    'lineNumber' in input &&
    typeof (input as AddPrCommentType).lineNumber === 'number' &&
    'comment' in input &&
    typeof (input as AddPrCommentType).comment === 'string' &&
    'side' in input &&
    ((input as AddPrCommentType).side === 'old' || (input as AddPrCommentType).side === 'new')
  );
}

// ── PR V2 draft helpers ─────────────────────────────────────────────

const PR_V2_DRAFT_NAVIGATION_MAX_ATTEMPTS = 20;
const PR_V2_DRAFT_NAVIGATION_RETRY_DELAY_MS = 100;

/** Maps add-comment tool input into the PR V2 draft state stored for a file. */
export function buildPrV2DraftFromAddPrComment(args: AddPrCommentType): PrV2FileDraftState {
  const isLineComment = args.subject_type !== 'file' && args.lineNumber > 0;
  const draftSide: PrV2GithubReviewSide | null = isLineComment
    ? commentSideToGithubReviewSide(args.side)
    : null;
  const hasMultilineRange =
    typeof args.start_line === 'number' &&
    args.start_line > 0 &&
    args.start_line !== args.lineNumber &&
    isLineComment;

  return {
    comment: args.comment ?? '',
    line: isLineComment ? String(args.lineNumber) : '',
    side: draftSide,
    startLine: hasMultilineRange ? String(args.start_line) : '',
    startSide: hasMultilineRange ? draftSide : null,
    selectedLines: null,
  };
}

function isSamePrV2DraftLocation(
  currentDraft: PrV2FileDraftState | undefined,
  args: AddPrCommentType,
): boolean {
  if (!currentDraft) {
    return false;
  }

  const expectedDraft = buildPrV2DraftFromAddPrComment(args);
  return (
    currentDraft.line === expectedDraft.line &&
    currentDraft.side === expectedDraft.side &&
    currentDraft.startLine === expectedDraft.startLine &&
    currentDraft.startSide === expectedDraft.startSide
  );
}

function schedulePrV2DraftNavigation(
  navigateToFile: DiffNavigationHandlers['navigateToFile'],
  filename: string,
) {
  if (typeof window === 'undefined') {
    return;
  }

  let attempts = 0;

  const tryNavigate = () => {
    const didNavigate = navigateToFile(filename, {
      source: 'programmatic',
      expandFile: true,
      scrollToDraft: true,
    });
    if (didNavigate) {
      return;
    }

    attempts += 1;
    if (attempts >= PR_V2_DRAFT_NAVIGATION_MAX_ATTEMPTS) {
      return;
    }

    // Discussion mounts the diff lazily, so keep retrying until navigation handlers exist.
    window.setTimeout(tryNavigate, PR_V2_DRAFT_NAVIGATION_RETRY_DELAY_MS);
  };

  window.setTimeout(tryNavigate, 0);
}

// ── PR V2 apply ─────────────────────────────────────────────────────

type ApplyAiPrCommentToPrV2Params = {
  args: AddPrCommentType;
  owner: string;
  repo: string;
  number: string;
  navigateToFile: DiffNavigationHandlers['navigateToFile'];
  getDraft: (prKey: string, filename: string) => PrV2FileDraftState | undefined;
  setDraftForFile: (prKey: string, filename: string, draft: PrV2FileDraftState | null) => void;
};

type ApplyAiPrCommentToPrV2Result = 'applied' | 'already-active' | 'missing-context';

/** Applies an AI-generated PR comment to the PR V2 draft store and opens the target file. */
export function applyAiPrCommentToPrV2({
  args,
  owner,
  repo,
  number,
  navigateToFile,
  getDraft,
  setDraftForFile,
}: ApplyAiPrCommentToPrV2Params): ApplyAiPrCommentToPrV2Result {
  if (!owner || !repo || !number) {
    return 'missing-context';
  }

  const prKey = getPrV2DraftStoreKey(owner, repo, number);
  const currentDraft = getDraft(prKey, args.filename);
  const isDraftAlreadyActive = isSamePrV2DraftLocation(currentDraft, args);

  if (!isDraftAlreadyActive) {
    setDraftForFile(prKey, args.filename, buildPrV2DraftFromAddPrComment(args));
  }

  dispatchPrV2TabHandoff(PR_V2_TABS.CHANGES);
  schedulePrV2DraftNavigation(navigateToFile, args.filename);

  return isDraftAlreadyActive ? 'already-active' : 'applied';
}

// ── Unified apply (PR V2 + legacy) ─────────────────────────────────

type ApplyAddPrCommentParams = {
  args: AddPrCommentType;
  owner: string;
  repo: string;
  number: string;
  navigateToFile: DiffNavigationHandlers['navigateToFile'];
  getDraft: (prKey: string, filename: string) => PrV2FileDraftState | undefined;
  setDraftForFile: (prKey: string, filename: string, draft: PrV2FileDraftState | null) => void;
  togglePrCommentInput: (args: AddPrCommentType | null) => void;
  activePrCommentInput: AddPrCommentType | null;
  activeTab: string;
  switchToChangesTab: () => void;
};

type ApplyAddPrCommentResult = 'applied' | 'already-active' | 'missing-context';

/**
 * Applies an AI-generated addPrComment tool call, handling both PR V2 and legacy routes.
 * Pure function (no hooks) — suitable for use in `onToolCall` or manual "Apply" button clicks.
 */
export function applyAddPrComment({
  args,
  owner,
  repo,
  number,
  navigateToFile,
  getDraft,
  setDraftForFile,
  togglePrCommentInput,
  activePrCommentInput,
  activeTab,
  switchToChangesTab,
}: ApplyAddPrCommentParams): ApplyAddPrCommentResult {
  // Ensure subject_type is line if we have a line number
  const commentData: AddPrCommentType = {
    ...args,
    subject_type: args.lineNumber > 0 ? 'line' : args.subject_type,
  };

  const pathname = typeof window === 'undefined' ? '' : window.location.pathname;

  if (isPrV2Route(pathname)) {
    const result = applyAiPrCommentToPrV2({
      args: commentData,
      owner,
      repo,
      number,
      navigateToFile,
      getDraft,
      setDraftForFile,
    });

    if (result === 'missing-context') {
      toast.error('Failed to open comment draft');
      return 'missing-context';
    }

    return result;
  }

  // Legacy PR route
  const isCommentAlreadyActive =
    activePrCommentInput &&
    activePrCommentInput.filename === commentData.filename &&
    activePrCommentInput.lineNumber === commentData.lineNumber &&
    activePrCommentInput.side === commentData.side;

  if (activeTab !== PR_TABS.CHANGES) {
    switchToChangesTab();
  }

  if (!isCommentAlreadyActive) {
    togglePrCommentInput(commentData);

    return 'applied';
  }

  return 'already-active';
}
